/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  Text,
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  Flex,
  Skeleton,
  Divider,
  HStack,
  IconButton,
  Badge,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { useQuery } from '@tanstack/react-query';
import { FaFileInvoice } from 'react-icons/fa6';
import { FunnelSimple } from 'phosphor-react';
import {
  Building2,
  FileText,
  ArrowUpRight,
  ArrowDownLeft,
  Landmark,
  Receipt,
  Ban,
  RefreshCw,
  LayoutGrid,
  List,
} from 'lucide-react';
import { createSearchParams, Link, useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import moment from 'moment';
import 'moment/locale/pt-br';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { SearchModeBar, MODES_ID_NOME_CNPJ } from '../../../../components/SearchModeBar';
import { FormBox } from '../../../../components/formBox';
import SelectInputForm from '../../../../components/inputs/forms/SelectInputForm';
import { IDataNoPagination, ISelect } from '../../../../types/shared';
import { ICompany } from '../../../../hooks/useCompanies';
import { IClient } from '../../../../hooks/useClients';
import { formatCNPJ } from '../../../../utils/FormatFunctions';
import { DataTable } from '../../../../components/datatable';
import { PageBox } from '../../../../components/pageBox';
import { INfseDashboard, useNfse, INfseDashboardEmpresa } from '../../../../hooks/useNfse';
import { FilterDrawer } from '../../../../components/FilterDrawer';
import { useMonthFilter } from '../../../../hooks/useMonthFilter';
import { MonthNavigator } from '../../../../components/shared/MonthNavigator';
import { NfseCompanyCard } from '../../../../components/NfseCompanyCard';

// Helper para formatar array de strings em opções de select
const getFormattedLabelStringFromArray = (arr?: string[]): ISelect<string>[] => {
  if (!arr) return [];
  return arr.map(item => ({ label: item, value: item }));
};

moment.locale('pt-br');

// Tipos de filtro do dashboard
type DashboardFilterType =
  | 'all'
  | 'prestadas'
  | 'tomadas'
  | 'ativas'
  | 'canceladas'
  | 'substituidas'
  | 'retencao_issqn'
  | 'retencao_pis_cofins' // tp_ret_pis_cofins = '1' (com retenção)
  | 'retencao_irrf'
  | 'retencao_csll'
  | 'retencao_inss'
  | 'retencao_federal'
  | 'retencao_municipal'
  | 'sem_retencao_federal'
  | 'sem_retencao_municipal';

interface StatCardProps {
  title: string;
  value: string | number;
  helpText?: string;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  isLoading?: boolean;
  onClick?: () => void;
  isActive?: boolean;
  isClickable?: boolean;
}

function StatCard({
  title,
  value,
  helpText,
  icon,
  iconColor,
  iconBg,
  isLoading,
  onClick,
  isActive,
  isClickable = false,
}: StatCardProps) {
  return (
    <Box
      bg="white"
      p={4}
      borderRadius="lg"
      shadow={isActive ? 'md' : 'sm'}
      border="2px solid"
      borderColor={isActive ? iconColor : 'gray.100'}
      cursor={isClickable ? 'pointer' : 'default'}
      onClick={isClickable ? onClick : undefined}
      transition="all 0.2s"
      _hover={
        isClickable
          ? {
              shadow: 'md',
              borderColor: iconColor,
              transform: 'translateY(-2px)',
            }
          : {}
      }
      position="relative"
    >
      {isActive && (
        <Box
          position="absolute"
          top={2}
          right={2}
          bg={iconColor}
          color="white"
          fontSize="xs"
          px={2}
          py={0.5}
          borderRadius="full"
          fontWeight="medium"
        >
          Filtrado
        </Box>
      )}
      <Flex
        position="absolute"
        top={2}
        right={2}
        align="center"
        justify="center"
        bg={iconBg}
        borderRadius="md"
        p={1.5}
        w="28px"
        h="28px"
      >
        <Icon as={icon} color={iconColor} boxSize={4} />
      </Flex>
      <Stat>
        <StatLabel color="gray.500" fontSize="sm">
          {title}
        </StatLabel>
        {isLoading ? (
          <Skeleton height="28px" width="80px" mt={2} />
        ) : (
          <StatNumber
            fontSize={{ base: 'md', md: 'lg', lg: 'xl' }}
            fontWeight="bold"
            color="gray.800"
          >
            {value}
          </StatNumber>
        )}
        {helpText && (
          <StatHelpText fontSize="xs" color="gray.400" mb={0}>
            {helpText}
          </StatHelpText>
        )}
      </Stat>
    </Box>
  );
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value);
}

// eslint-disable-next-line no-unused-vars
// type SelectMonthHandler = (year: number, month: number) => void;

// interface MonthNavigatorProps {
//   currentDate: moment.Moment;
//   onPrevMonth: () => void;
//   onNextMonth: () => void;
//   onSelectMonth: SelectMonthHandler;
// }

// function MonthNavigator({
//   currentDate,
//   onPrevMonth,
//   onNextMonth,
//   onSelectMonth,
// }: MonthNavigatorProps) {
//   const monthYear = currentDate.format('MMMM [de] YYYY');
//   const isCurrentMonth = currentDate.isSame(moment(), 'month');
//   const [selectedYear, setSelectedYear] = useState(currentDate.year());

//   const months = [
//     'Jan',
//     'Fev',
//     'Mar',
//     'Abr',
//     'Mai',
//     'Jun',
//     'Jul',
//     'Ago',
//     'Set',
//     'Out',
//     'Nov',
//     'Dez',
//   ];

//   const currentYear = moment().year();
//   const currentMonth = moment().month();

//   const handleYearChange = (direction: 'prev' | 'next') => {
//     if (direction === 'prev') {
//       setSelectedYear(prev => prev - 1);
//     } else if (direction === 'next' && selectedYear < currentYear) {
//       setSelectedYear(prev => prev + 1);
//     }
//   };

//   const isMonthDisabled = (monthIndex: number) => {
//     return selectedYear === currentYear && monthIndex > currentMonth;
//   };

//   const isMonthSelected = (monthIndex: number) => {
//     return selectedYear === currentDate.year() && monthIndex === currentDate.month();
//   };

//   return (
//     <HStack spacing={2} bg="white" px={4} borderRadius="lg" shadow="sm" height="38px">
//       <IconButton
//         aria-label="Mês anterior"
//         icon={<Icon as={ChevronLeft} />}
//         onClick={onPrevMonth}
//         variant="ghost"
//         size="sm"
//         colorScheme="blue"
//       />
//       <Popover placement="bottom">
//         <PopoverTrigger>
//           <HStack
//             spacing={2}
//             minW="180px"
//             justify="center"
//             cursor="pointer"
//             _hover={{ bg: 'gray.50' }}
//             px={2}
//             py={1}
//             borderRadius="md"
//           >
//             <Icon as={Calendar} color="gray.500" boxSize={4} />
//             <Text fontWeight="medium" fontSize="sm" textTransform="capitalize" color="gray.700">
//               {monthYear}
//             </Text>
//           </HStack>
//         </PopoverTrigger>
//         <PopoverContent width="280px">
//           <PopoverBody p={4}>
//             {/* Seletor de Ano */}
//             <HStack justify="space-between" mb={4}>
//               <IconButton
//                 aria-label="Ano anterior"
//                 icon={<Icon as={ChevronLeft} boxSize={4} />}
//                 onClick={() => handleYearChange('prev')}
//                 variant="ghost"
//                 size="sm"
//               />
//               <Text fontWeight="bold" fontSize="md">
//                 {selectedYear}
//               </Text>
//               <IconButton
//                 aria-label="Próximo ano"
//                 icon={<Icon as={ChevronRight} boxSize={4} />}
//                 onClick={() => handleYearChange('next')}
//                 variant="ghost"
//                 size="sm"
//                 isDisabled={selectedYear >= currentYear}
//               />
//             </HStack>
//             {/* Grid de Meses */}
//             <Grid templateColumns="repeat(4, 1fr)" gap={2}>
//               {months.map((month, index) => (
//                 <Button
//                   key={month}
//                   size="sm"
//                   variant={isMonthSelected(index) ? 'solid' : 'ghost'}
//                   colorScheme={isMonthSelected(index) ? 'blue' : 'gray'}
//                   isDisabled={isMonthDisabled(index)}
//                   onClick={() => {
//                     onSelectMonth(selectedYear, index);
//                   }}
//                   fontSize="xs"
//                 >
//                   {month}
//                 </Button>
//               ))}
//             </Grid>
//           </PopoverBody>
//         </PopoverContent>
//       </Popover>
//       <IconButton
//         aria-label="Próximo mês"
//         icon={<Icon as={ChevronRight} />}
//         onClick={onNextMonth}
//         variant="ghost"
//         size="sm"
//         colorScheme="blue"
//         isDisabled={isCurrentMonth}
//       />
//     </HStack>
//   );
// }

export function ModulesHomeIntegrationNfseManagementPage() {
  const { getNfseDashboard, getNfseDashboardFilters } = useNfse();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [dashboardFilter, setDashboardFilter] = useState<DashboardFilterType>('all');
  const [searchMode, setSearchMode] = useState<'ID' | 'Nome' | 'CNPJ'>('ID');
  const searchModeRef = useRef(searchMode);

  // Estado para modo de visualização (grid/list)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Hook para navegação
  const navigate = useNavigate();

  useEffect(() => {
    searchModeRef.current = searchMode;
  }, [searchMode]);

  const { currentDate, handlePrevMonth, handleNextMonth, handleSelectMonth, filters, setFilters } =
    useMonthFilter({ additionalParams: { search: '' } });

  // Verificar se há filtros ativos
  const hasActiveFilters = Array.from(filters.keys()).some(
    key =>
      key !== 'page' &&
      key !== 'size' &&
      key !== 'date_start' &&
      key !== 'date_end' &&
      key !== 'search' &&
      key !== 'cnpj' &&
      filters.get(key),
  );

  // Função para limpar todos os filtros
  const handleClearFilters = () => {
    const newFilters = new URLSearchParams();
    newFilters.set('page', '1');
    newFilters.set('size', '50');
    newFilters.set('search', filters.get('search') || '');
    if (filters.get('cnpj')) newFilters.set('cnpj', filters.get('cnpj') || '');
    newFilters.set(
      'date_start',
      filters.get('date_start') || moment().startOf('month').format('YYYY-MM-DD'),
    );
    newFilters.set(
      'date_end',
      filters.get('date_end') || moment().endOf('month').format('YYYY-MM-DD'),
    );
    setFilters(newFilters);
    // Limpar cada campo individualmente para forçar atualização visual
    const { setValue } = filtersFormMethods;
    setValue('companies', null as any);
    setValue('cidade', null as any);
    setValue('uf', null as any);
    setValue('grupo_de_empresas', null as any);
    setValue('tags', null as any);
    setValue('comercial', null as any);
    setValue('contabil', null as any);
    setValue('financeiro', null as any);
    setValue('contas_a_pagar', null as any);
    setValue('encerramento_anual', null as any);
    setValue('fiscal', null as any);
    setValue('imposto_de_renda', null as any);
    setValue('integracao_contabil', null as any);
    setValue('integracao_fiscal', null as any);
    setValue('pessoal', null as any);
    setValue('relacionamento_com_cliente', null as any);
    setValue('societario', null as any);
  };

  // Função para aplicar os filtros quando clica no botão "Filtrar"
  const handleApplyFilters = () => {
    const values = filtersFormMethods.getValues();
    const newFilters = new URLSearchParams();

    // Mantém filtros básicos
    newFilters.set('page', '1');
    newFilters.set('size', '50');
    newFilters.set('search', filters.get('search') || '');
    if (filters.get('cnpj')) newFilters.set('cnpj', filters.get('cnpj') || '');
    newFilters.set(
      'date_start',
      filters.get('date_start') || moment().startOf('month').format('YYYY-MM-DD'),
    );
    newFilters.set(
      'date_end',
      filters.get('date_end') || moment().endOf('month').format('YYYY-MM-DD'),
    );

    // Aplica filtros avançados
    if (values.companies?.external_id) newFilters.set('id', values.companies.external_id);
    if (values.cidade?.value) newFilters.set('cidade', values.cidade.value);
    if (values.uf?.value) newFilters.set('uf', values.uf.value);
    if (values.grupo_de_empresas?.value)
      newFilters.set('grupo_de_empresas', values.grupo_de_empresas.value);
    if (values.tags?.value) newFilters.set('tags', values.tags.value);
    if (values.comercial?.value) newFilters.set('comercial', values.comercial.value);
    if (values.contabil?.value) newFilters.set('contabil', values.contabil.value);
    if (values.financeiro?.value) newFilters.set('financeiro', values.financeiro.value);
    if (values.contas_a_pagar?.value) newFilters.set('contas_a_pagar', values.contas_a_pagar.value);
    if (values.encerramento_anual?.value)
      newFilters.set('encerramento_anual', values.encerramento_anual.value);
    if (values.fiscal?.value) newFilters.set('fiscal', values.fiscal.value);
    if (values.imposto_de_renda?.value)
      newFilters.set('imposto_de_renda', values.imposto_de_renda.value);
    if (values.integracao_contabil?.value)
      newFilters.set('integracao_contabil', values.integracao_contabil.value);
    if (values.integracao_fiscal?.value)
      newFilters.set('integracao_fiscal', values.integracao_fiscal.value);
    if (values.pessoal?.value) newFilters.set('pessoal', values.pessoal.value);
    if (values.relacionamento_com_cliente?.value)
      newFilters.set('relacionamento_com_cliente', values.relacionamento_com_cliente.value);
    if (values.societario?.value) newFilters.set('societario', values.societario.value);

    setFilters(newFilters);
  };

  const columns: ColumnDef<INfseDashboardEmpresa>[] = [
    {
      accessorKey: 'codi_emp',
      header: 'ID',
      size: 80,
    },
    {
      accessorKey: 'razao_social',
      header: 'Razão Social',
    },
    {
      accessorKey: 'cnpj',
      header: 'CNPJ',
      cell: ({ row }) => {
        const value = row.original.cnpj;
        return formatCNPJ(value || '');
      },
    },
    {
      accessorKey: 'total_notas',
      header: 'Notas',
      size: 80,
      cell: ({ row }) => {
        const totalNotas = row.original.total_notas;
        return (
          <Text fontWeight="medium" textAlign="center">
            {totalNotas}
          </Text>
        );
      },
    },
    {
      id: 'prestadas_tomadas',
      header: 'Prest./Tom.',
      size: 100,
      cell: ({ row }) => {
        const totalPrestadas = row.original.total_prestadas;
        const totalTomadas = row.original.total_tomadas;
        return (
          <HStack spacing={2} justify="center">
            <Flex align="center" gap={1}>
              <Icon as={ArrowUpRight} color="green.500" boxSize={4} />
              <Text fontSize="sm" color="green.600" fontWeight="medium">
                {totalPrestadas}
              </Text>
            </Flex>
            <Flex align="center" gap={1}>
              <Icon as={ArrowDownLeft} color="orange.500" boxSize={4} />
              <Text fontSize="sm" color="orange.600" fontWeight="medium">
                {totalTomadas}
              </Text>
            </Flex>
          </HStack>
        );
      },
    },
    {
      accessorKey: 'valor_total',
      header: 'Valor Total',
      cell: ({ row }) => {
        const valorTotal = row.original.valor_total;
        return (
          <Text fontWeight="bold" color="green.600">
            {formatCurrency(valorTotal)}
          </Text>
        );
      },
    },
    {
      id: 'retencoes',
      header: 'Retenções',
      cell: ({ row }) => {
        const empresa = row.original;
        const temRetencaoFederal =
          empresa.tem_retencao_pis_cofins_tp2 ||
          empresa.tem_retencao_irrf ||
          empresa.tem_retencao_csll ||
          empresa.tem_retencao_inss;
        const temRetencaoMunicipal = empresa.tem_retencao_issqn;

        if (!temRetencaoFederal && !temRetencaoMunicipal) {
          return (
            <Badge colorScheme="gray" fontSize="xs" px={2} py={1} borderRadius="md">
              Sem retenções
            </Badge>
          );
        }

        return (
          <HStack spacing={1} flexWrap="wrap">
            {empresa.tem_retencao_issqn && (
              <Badge colorScheme="red" fontSize="xs" px={1}>
                ISSQN
              </Badge>
            )}
            {empresa.tem_retencao_pis_cofins_tp2 && (
              <Badge colorScheme="blue" fontSize="xs" px={1}>
                PIS/COFINS
              </Badge>
            )}
            {empresa.tem_retencao_irrf && (
              <Badge colorScheme="yellow" fontSize="xs" px={1}>
                IRRF
              </Badge>
            )}
            {empresa.tem_retencao_csll && (
              <Badge colorScheme="teal" fontSize="xs" px={1}>
                CSLL
              </Badge>
            )}
            {empresa.tem_retencao_inss && (
              <Badge colorScheme="purple" fontSize="xs" px={1}>
                INSS
              </Badge>
            )}
          </HStack>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      size: 100,
      cell: ({ row }) => {
        const empresa = row.original;
        const cellDateStart = filters.get('date_start');
        const cellDateEnd = filters.get('date_end');

        return (
          <Link
            to={{
              pathname: `${empresa.codi_emp}`,
              search: createSearchParams({
                date_start: cellDateStart || moment().startOf('month').format('YYYY-MM-DD'),
                date_end: cellDateEnd || moment().endOf('month').format('YYYY-MM-DD'),
              }).toString(),
            }}
            state={{ company: empresa }}
          >
            <Button
              variant="outline"
              size="sm"
              borderRadius="full"
              borderColor="#718EBF"
              color="#718EBF"
            >
              Ver NFS-e
            </Button>
          </Link>
        );
      },
    },
  ];

  const filtersFormMethods = useForm<{
    search: string;
    cnpj: string;
    companyId: any;
    companies: ICompany;
    cliente: IClient;
    cidade: ISelect<string>;
    uf: ISelect<string>;
    grupo_de_empresas: ISelect<string>;
    tags: ISelect<string>;
    comercial: ISelect<string>;
    contabil: ISelect<string>;
    financeiro: ISelect<string>;
    contas_a_pagar: ISelect<string>;
    encerramento_anual: ISelect<string>;
    fiscal: ISelect<string>;
    imposto_de_renda: ISelect<string>;
    integracao_contabil: ISelect<string>;
    integracao_fiscal: ISelect<string>;
    pessoal: ISelect<string>;
    relacionamento_com_cliente: ISelect<string>;
    societario: ISelect<string>;
  }>({
    mode: 'onChange',
    defaultValues: {
      search: filters.get('search') ?? '',
      cnpj: filters.get('cnpj') ?? '',
    },
  });

  // Debounce para o campo de busca com modo selecionável
  const debouncedSearch = useDebounceCallback((searchValue: string) => {
    const newFilters = new URLSearchParams(filters);
    const currentMode = searchModeRef.current;

    // Remove filtros anteriores
    newFilters.delete('search');
    newFilters.delete('cnpj');
    newFilters.delete('external_id');

    if (searchValue) {
      switch (currentMode) {
        case 'ID':
          newFilters.set('external_id', searchValue);
          break;
        case 'Nome':
          newFilters.set('search', searchValue);
          break;
        case 'CNPJ':
          newFilters.set('cnpj', searchValue.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
          break;
        default:
          break;
      }
    }
    newFilters.set('page', '1');
    setFilters(newFilters);
  }, 500);

  // Watch no campo search para aplicar debounce
  useEffect(() => {
    const subscription = filtersFormMethods.watch((values, { name }) => {
      if (name === 'search') {
        debouncedSearch(values.search || '');
      }
    });

    return () => subscription.unsubscribe();
  }, [filtersFormMethods.watch, debouncedSearch]);

  // Re-executa a busca quando o modo de pesquisa muda
  useEffect(() => {
    debouncedSearch(filtersFormMethods.getValues().search || '');
  }, [searchMode]);

  // Filtros específicos do dashboard NFS-e (apenas empresas com NFS-e no período)
  const nfseDashboardFiltersParams = {
    date_start: filters.get('date_start') ?? moment().startOf('month').format('YYYY-MM-DD'),
    date_end: filters.get('date_end') ?? moment().endOf('month').format('YYYY-MM-DD'),
  };

  const { data: NfseFiltersResponse } = useQuery<IDataNoPagination<Record<string, string[]>>>({
    queryKey: ['NfseDashboardFilters', nfseDashboardFiltersParams],
    queryFn: getNfseDashboardFilters,
  });

  const NfseFilters = NfseFiltersResponse?.data;

  // Query para o dashboard de NFS-e (fonte principal de empresas com NFS-e no período)
  const dashboardFilters = React.useMemo(() => {
    const dashFilters: Record<string, string> = {
      date_start: filters.get('date_start') ?? moment().startOf('month').format('YYYY-MM-DD'),
      date_end: filters.get('date_end') ?? moment().endOf('month').format('YYYY-MM-DD'),
    };

    // Adiciona filtros avançados de empresa
    const filterKeys = [
      'id',
      'cidade',
      'uf',
      'grupo_de_empresas',
      'tags',
      'comercial',
      'contabil',
      'financeiro',
      'contas_a_pagar',
      'encerramento_anual',
      'fiscal',
      'imposto_de_renda',
      'integracao_contabil',
      'integracao_fiscal',
      'pessoal',
      'relacionamento_com_cliente',
      'societario',
    ];

    filterKeys.forEach(key => {
      const value = filters.get(key);
      if (value) {
        dashFilters[key] = value;
      }
    });

    return dashFilters;
  }, [filters]);

  const { data: dashboardData, isFetching: isFetchingDashboard } = useQuery<
    IDataNoPagination<INfseDashboard>
  >({
    queryKey: ['NfseDashboard', dashboardFilters],
    queryFn: getNfseDashboard,
  });

  const dashboard = dashboardData?.data;

  // Cálculos consolidados para insights de retenções
  // Retenção federal: NÃO inclui PIS/COFINS (apenas IRRF, CSLL, INSS)
  const empresasComRetencaoFederal = React.useMemo(() => {
    if (!dashboard?.empresas_detalhes) return 0;
    return dashboard.empresas_detalhes.filter(
      empresa =>
        empresa.tem_retencao_irrf || empresa.tem_retencao_csll || empresa.tem_retencao_inss,
    ).length;
  }, [dashboard?.empresas_detalhes]);

  // Empresas com RET. PIS/COFINS (código tpRetPisCofins de retenção — NT-007)
  const empresasComRetencaoPisCofins = React.useMemo(() => {
    if (!dashboard?.empresas_detalhes) return 0;
    return dashboard.empresas_detalhes.filter(empresa => empresa.tem_retencao_pis_cofins_tp2)
      .length;
  }, [dashboard?.empresas_detalhes]);

  const empresasComRetencaoMunicipal = React.useMemo(() => {
    if (!dashboard?.empresas_detalhes) return 0;
    return dashboard.empresas_detalhes.filter(empresa => empresa.tem_retencao_issqn).length;
  }, [dashboard?.empresas_detalhes]);

  // Valor retido federal de verdade: IRRF + CSLL + INSS (v_ret_*). PIS/COFINS
  // NÃO entram na soma: v_pis/v_cofins são débito de apuração própria (NT-007)
  // e o que for retido de PIS/COFINS já vem somado dentro da CSLL (vRetCSLL).
  const valorTotalRetencoesFederais = React.useMemo(() => {
    return (
      (dashboard?.valor_total_irrf ?? 0) +
      (dashboard?.valor_total_csll ?? 0) +
      (dashboard?.valor_total_inss ?? 0)
    );
  }, [dashboard]);

  const empresasSemRetencaoFederal = React.useMemo(() => {
    if (!dashboard?.empresas_detalhes) return 0;
    return dashboard.empresas_detalhes.filter(
      empresa =>
        !empresa.tem_retencao_irrf && !empresa.tem_retencao_csll && !empresa.tem_retencao_inss,
    ).length;
  }, [dashboard?.empresas_detalhes]);

  const empresasSemRetencaoMunicipal = React.useMemo(() => {
    if (!dashboard?.empresas_detalhes) return 0;
    return dashboard.empresas_detalhes.filter(empresa => !empresa.tem_retencao_issqn).length;
  }, [dashboard?.empresas_detalhes]);

  // Filtra as empresas com base no dashboard (empresas com NFS-e no período)
  // e aplica filtros adicionais do dashboard (prestadas, tomadas, retenções)
  const filteredCompaniesList = React.useMemo(() => {
    if (!dashboard?.empresas_detalhes) {
      return [];
    }

    // Obter busca do filtro por modo
    const searchTerm = filters.get('search')?.toLowerCase() ?? '';
    const externalIdTerm = filters.get('external_id') ?? '';
    const cnpjTerm =
      filters
        .get('cnpj')
        ?.replace(/[^0-9A-Za-z]/g, '')
        .toUpperCase() ?? '';

    // Filtrar empresas do dashboard (já vem filtrado pelo backend)
    let filteredEmpresas = dashboard.empresas_detalhes;

    // Aplicar filtro de busca local por ID
    if (externalIdTerm) {
      filteredEmpresas = filteredEmpresas.filter(
        empresa => empresa.codi_emp?.toString() === externalIdTerm,
      );
    }
    // Aplicar filtro de busca local por Nome
    else if (searchTerm) {
      filteredEmpresas = filteredEmpresas.filter(empresa =>
        empresa.razao_social?.toLowerCase().includes(searchTerm),
      );
    }
    // Aplicar filtro de busca local por CNPJ
    else if (cnpjTerm) {
      filteredEmpresas = filteredEmpresas.filter(empresa =>
        empresa.cnpj
          ?.replace(/[^0-9A-Za-z]/g, '')
          .toUpperCase()
          .includes(cnpjTerm),
      );
    }

    // Aplicar filtro do dashboard (cards clicáveis)
    if (dashboardFilter !== 'all') {
      filteredEmpresas = filteredEmpresas.filter(empresa => {
        switch (dashboardFilter) {
          case 'prestadas':
            return empresa.total_prestadas > 0;
          case 'tomadas':
            return empresa.total_tomadas > 0;
          case 'ativas':
            return (
              empresa.total_notas - empresa.total_canceladas - (empresa.total_substituidas || 0) > 0
            );
          case 'canceladas':
            return empresa.total_canceladas > 0;
          case 'substituidas':
            return (empresa.total_substituidas || 0) > 0;
          case 'retencao_issqn':
            return empresa.tem_retencao_issqn;
          case 'retencao_pis_cofins':
            return empresa.tem_retencao_pis_cofins_tp2;
          case 'retencao_irrf':
            return empresa.tem_retencao_irrf;
          case 'retencao_csll':
            return empresa.tem_retencao_csll;
          case 'retencao_inss':
            return empresa.tem_retencao_inss;
          case 'retencao_federal':
            return (
              empresa.tem_retencao_irrf || empresa.tem_retencao_csll || empresa.tem_retencao_inss
            );
          case 'retencao_municipal':
            return empresa.tem_retencao_issqn;
          case 'sem_retencao_federal':
            return (
              !empresa.tem_retencao_irrf && !empresa.tem_retencao_csll && !empresa.tem_retencao_inss
            );
          case 'sem_retencao_municipal':
            return !empresa.tem_retencao_issqn;
          default:
            return true;
        }
      });
    }

    return filteredEmpresas;
  }, [dashboard?.empresas_detalhes, dashboardFilter, filters]);

  // Toggle do filtro do dashboard
  // Empresas com ao menos uma nota substituída. Contado na lista para bater com o filtro
  // do card: o empresas_com_notas_substituidas do backend deixa de fora quem também
  // tem cancelamento comum.
  const empresasComSubstituidas = dashboard?.empresas_detalhes
    ? dashboard.empresas_detalhes.filter(empresa => (empresa.total_substituidas || 0) > 0).length
    : (dashboard?.empresas_com_notas_substituidas ?? 0);

  const handleDashboardFilterClick = (filterType: DashboardFilterType) => {
    if (dashboardFilter === filterType) {
      setDashboardFilter('all');
    } else {
      setDashboardFilter(filterType);
    }
  };

  // Handler para clique no card (modo grid) - navega para página de detalhes
  const handleCardClick = (empresa: INfseDashboardEmpresa) => {
    const dateStart = filters.get('date_start') || moment().startOf('month').format('YYYY-MM-DD');
    const dateEnd = filters.get('date_end') || moment().endOf('month').format('YYYY-MM-DD');

    navigate(
      {
        pathname: `${empresa.codi_emp}`,
        search: createSearchParams({
          date_start: dateStart,
          date_end: dateEnd,
        }).toString(),
      },
      {
        state: {
          company: {
            external_id: empresa.codi_emp,
            nome: empresa.razao_social,
            razao_social: empresa.razao_social,
            cnpj: empresa.cnpj,
          },
        },
      },
    );
  };

  // Obter a data atual do filtro como objeto moment
  // const currentFilterDate = moment(filters.get('date_start') ?? moment().startOf('month'));

  // Funções para navegar entre meses
  // const handlePrevMonth = () => {
  //   const prevMonth = currentFilterDate.clone().subtract(1, 'month');
  //   setFilters(
  //     createSearchParams({
  //       ...Object.fromEntries(filters),
  //       date_start: prevMonth.startOf('month').format('YYYY-MM-DD'),
  //       date_end: prevMonth.endOf('month').format('YYYY-MM-DD'),
  //     }),
  //   );
  // };

  // const handleNextMonth = () => {
  //   const nextMonth = currentFilterDate.clone().add(1, 'month');
  //   if (!nextMonth.isAfter(moment(), 'month')) {
  //     setFilters(
  //       createSearchParams({
  //         ...Object.fromEntries(filters),
  //         date_start: nextMonth.startOf('month').format('YYYY-MM-DD'),
  //         date_end: nextMonth.endOf('month').format('YYYY-MM-DD'),
  //       }),
  //     );
  //   }
  // };

  // const handleSelectMonth = (year: number, month: number) => {
  //   const selectedDate = moment().year(year).month(month);
  //   setFilters(
  //     createSearchParams({
  //       ...Object.fromEntries(filters),
  //       date_start: selectedDate.startOf('month').format('YYYY-MM-DD'),
  //       date_end: selectedDate.endOf('month').format('YYYY-MM-DD'),
  //     }),
  //   );
  // };

  return (
    <PageBox>
      {/* Header com título */}
      <div className="flex content-start pb-5 md:w-1/3 lg:content-center ">
        <ReturnButton previousPage="modulesHome/integration" />

        <div className="h-full">
          <div className="flex items-center justify-center rounded-full bg-[#E3F2FD] p-3">
            <FaFileInvoice color="#1976D2" className="size-8" />
          </div>
        </div>
        <div className="flex w-full flex-col justify-center gap-1 pl-2">
          <Text fontWeight="bold" fontSize="16px">
            Portal Nacional
          </Text>
          <Text fontSize="12px" color="gray.500">
            Visualize as notas fiscais de serviço eletrônicas por empresa
          </Text>
        </div>
      </div>

      {/* Dashboard de Insights */}
      <Box mb={6}>
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="lg" fontWeight="bold" color="gray.700">
            Resumo do Período
          </Text>
          {dashboardFilter !== 'all' && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="red"
              onClick={() => setDashboardFilter('all')}
            >
              Limpar Filtro
            </Button>
          )}
        </Flex>

        {/* Primeira linha - Totais gerais */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4} mb={4}>
          <StatCard
            title="Total de Empresas"
            value={dashboard?.total_empresas ?? 0}
            helpText="Empresas com NFS-e no período"
            icon={Building2}
            iconColor="#3182CE"
            iconBg="#EBF8FF"
            isLoading={isFetchingDashboard}
          />
          <StatCard
            title="Total de Notas"
            value={dashboard?.total_notas?.toLocaleString('pt-BR') ?? 0}
            helpText={`${dashboard?.total_notas_prestadas ?? 0} prestadas | ${dashboard?.total_notas_tomadas ?? 0} tomadas`}
            icon={FileText}
            iconColor="#38A169"
            iconBg="#F0FFF4"
            isLoading={isFetchingDashboard}
          />
          <StatCard
            title="Notas Ativas"
            value={dashboard?.total_notas_ativas?.toLocaleString('pt-BR') ?? 0}
            helpText="Clique para filtrar"
            icon={FileText}
            iconColor="#38A169"
            iconBg="#F0FFF4"
            isLoading={isFetchingDashboard}
            isClickable
            isActive={dashboardFilter === 'ativas'}
            onClick={() => handleDashboardFilterClick('ativas')}
          />
          <StatCard
            title="Notas Canceladas"
            value={dashboard?.total_notas_canceladas ?? 0}
            helpText={`${dashboard?.empresas_com_notas_canceladas ?? 0} empresas`}
            icon={Ban}
            iconColor="#E53E3E"
            iconBg="#FFF5F5"
            isLoading={isFetchingDashboard}
            isClickable
            isActive={dashboardFilter === 'canceladas'}
            onClick={() => handleDashboardFilterClick('canceladas')}
          />
          <StatCard
            title="Notas Substituídas"
            value={dashboard?.total_notas_substituidas?.toLocaleString('pt-BR') ?? 0}
            helpText={`${empresasComSubstituidas} empresas`}
            icon={RefreshCw}
            iconColor="#805AD5"
            iconBg="#FAF5FF"
            isLoading={isFetchingDashboard}
            isClickable
            isActive={dashboardFilter === 'substituidas'}
            onClick={() => handleDashboardFilterClick('substituidas')}
          />
        </SimpleGrid>

        {/* Segunda linha - Valores e Empresas por tipo */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={4}>
          <StatCard
            title="Valor Total Ativas"
            value={formatCurrency(dashboard?.valor_total_ativas ?? 0)}
            helpText="Soma das notas ativas"
            icon={Receipt}
            iconColor="#38A169"
            iconBg="#F0FFF4"
            isLoading={isFetchingDashboard}
          />
          <StatCard
            title="Valor Total Canceladas"
            value={formatCurrency(dashboard?.valor_total_canceladas ?? 0)}
            helpText="Soma das notas canceladas (sem substituição)"
            icon={Receipt}
            iconColor="#E53E3E"
            iconBg="#FFF5F5"
            isLoading={isFetchingDashboard}
          />
          <StatCard
            title="Valor Total Substituídas"
            value={formatCurrency(dashboard?.valor_total_substituidas ?? 0)}
            helpText="Soma das notas substituídas"
            icon={Receipt}
            iconColor="#805AD5"
            iconBg="#FAF5FF"
            isLoading={isFetchingDashboard}
          />
          <StatCard
            title="Valor Total Geral"
            value={formatCurrency(dashboard?.valor_total ?? 0)}
            helpText="Soma de todas as notas"
            icon={Receipt}
            iconColor="#2F855A"
            iconBg="#F0FFF4"
            isLoading={isFetchingDashboard}
          />
        </SimpleGrid>

        {/* Terceira linha - Empresas por tipo */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={4}>
          <StatCard
            title="Empresas c/ Notas Prestadas"
            value={dashboard?.empresas_com_notas_prestadas ?? 0}
            helpText="Clique para filtrar"
            icon={ArrowUpRight}
            iconColor="#DD6B20"
            iconBg="#FFFAF0"
            isLoading={isFetchingDashboard}
            isClickable
            isActive={dashboardFilter === 'prestadas'}
            onClick={() => handleDashboardFilterClick('prestadas')}
          />
          <StatCard
            title="Empresas c/ Notas Tomadas"
            value={dashboard?.empresas_com_notas_tomadas ?? 0}
            helpText="Clique para filtrar"
            icon={ArrowDownLeft}
            iconColor="#805AD5"
            iconBg="#FAF5FF"
            isLoading={isFetchingDashboard}
            isClickable
            isActive={dashboardFilter === 'tomadas'}
            onClick={() => handleDashboardFilterClick('tomadas')}
          />
        </SimpleGrid>

        <Divider my={4} />

        {/* Insights de Retenções - Cards Consolidados */}
        <Text fontSize="md" fontWeight="semibold" mb={3} color="gray.600">
          Insights de Retenções
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mb={4}>
          <StatCard
            title="Empresas c/ Ret. Federais"
            value={empresasComRetencaoFederal}
            helpText="IRRF, CSLL ou INSS (PIS/COFINS tem card próprio)"
            icon={Landmark}
            iconColor="#2B6CB0"
            iconBg="#EBF8FF"
            isLoading={isFetchingDashboard}
            isClickable
            isActive={dashboardFilter === 'retencao_federal'}
            onClick={() => handleDashboardFilterClick('retencao_federal')}
          />
          <StatCard
            title="Empresas s/ Ret. Federais"
            value={empresasSemRetencaoFederal}
            helpText="Sem IRRF, CSLL e INSS"
            icon={Landmark}
            iconColor="#718096"
            iconBg="#EDF2F7"
            isLoading={isFetchingDashboard}
            isClickable
            isActive={dashboardFilter === 'sem_retencao_federal'}
            onClick={() => handleDashboardFilterClick('sem_retencao_federal')}
          />
          <StatCard
            title="Valor Ret. Federais"
            value={formatCurrency(valorTotalRetencoesFederais)}
            helpText="IRRF + CSLL + INSS retidos (notas ativas)"
            icon={Receipt}
            iconColor="#2B6CB0"
            iconBg="#EBF8FF"
            isLoading={isFetchingDashboard}
          />
          <StatCard
            title="Empresas c/ Ret. Municipais"
            value={empresasComRetencaoMunicipal}
            helpText="ISSQN retido"
            icon={Landmark}
            iconColor="#C53030"
            iconBg="#FFF5F5"
            isLoading={isFetchingDashboard}
            isClickable
            isActive={dashboardFilter === 'retencao_municipal'}
            onClick={() => handleDashboardFilterClick('retencao_municipal')}
          />
          <StatCard
            title="Empresas s/ Ret. Municipais"
            value={empresasSemRetencaoMunicipal}
            helpText="Sem ISSQN retido"
            icon={Landmark}
            iconColor="#718096"
            iconBg="#EDF2F7"
            isLoading={isFetchingDashboard}
            isClickable
            isActive={dashboardFilter === 'sem_retencao_municipal'}
            onClick={() => handleDashboardFilterClick('sem_retencao_municipal')}
          />
          <StatCard
            title="Valor Ret. Municipais"
            value={formatCurrency(dashboard?.valor_total_issqn ?? 0)}
            helpText="ISSQN retido (notas ativas)"
            icon={Receipt}
            iconColor="#C53030"
            iconBg="#FFF5F5"
            isLoading={isFetchingDashboard}
          />
        </SimpleGrid>

        <Divider my={4} />

        {/* Detalhamento das Retenções Federais */}
        <Text fontSize="md" fontWeight="semibold" mb={3} color="gray.600">
          Detalhamento das Retenções Federais
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={4}>
          <StatCard
            title="RET. PIS/COFINS"
            value={empresasComRetencaoPisCofins}
            helpText="Clique para filtrar"
            icon={Landmark}
            iconColor="#2B6CB0"
            iconBg="#EBF8FF"
            isLoading={isFetchingDashboard}
            isClickable
            isActive={dashboardFilter === 'retencao_pis_cofins'}
            onClick={() => handleDashboardFilterClick('retencao_pis_cofins')}
          />
          <StatCard
            title="Empresas c/ Ret. IRRF"
            value={dashboard?.empresas_com_retencao_irrf ?? 0}
            helpText="Clique para filtrar"
            icon={Landmark}
            iconColor="#D69E2E"
            iconBg="#FFFFF0"
            isLoading={isFetchingDashboard}
            isClickable
            isActive={dashboardFilter === 'retencao_irrf'}
            onClick={() => handleDashboardFilterClick('retencao_irrf')}
          />
          <StatCard
            title="Empresas c/ Ret. CSLL"
            value={dashboard?.empresas_com_retencao_csll ?? 0}
            helpText="Clique para filtrar"
            icon={Landmark}
            iconColor="#319795"
            iconBg="#E6FFFA"
            isLoading={isFetchingDashboard}
            isClickable
            isActive={dashboardFilter === 'retencao_csll'}
            onClick={() => handleDashboardFilterClick('retencao_csll')}
          />
          <StatCard
            title="Empresas c/ Ret. INSS"
            value={dashboard?.empresas_com_retencao_inss ?? 0}
            helpText="Clique para filtrar"
            icon={Landmark}
            iconColor="#9F7AEA"
            iconBg="#FAF5FF"
            isLoading={isFetchingDashboard}
            isClickable
            isActive={dashboardFilter === 'retencao_inss'}
            onClick={() => handleDashboardFilterClick('retencao_inss')}
          />
        </SimpleGrid>

        {/* Quinta linha - Valores dos Impostos Federais */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          <StatCard
            title="Débito PIS + COFINS"
            value={formatCurrency(
              (dashboard?.valor_total_pis ?? 0) + (dashboard?.valor_total_cofins ?? 0),
            )}
            helpText={`Apuração própria, não é retenção — PIS: ${formatCurrency(dashboard?.valor_total_pis ?? 0)} | COFINS: ${formatCurrency(dashboard?.valor_total_cofins ?? 0)}`}
            icon={Receipt}
            iconColor="#2B6CB0"
            iconBg="#EBF8FF"
            isLoading={isFetchingDashboard}
          />
          <StatCard
            title="Valor IRRF"
            value={formatCurrency(dashboard?.valor_total_irrf ?? 0)}
            helpText="Total de IR Retido na Fonte"
            icon={Receipt}
            iconColor="#D69E2E"
            iconBg="#FFFFF0"
            isLoading={isFetchingDashboard}
          />
          <StatCard
            title="Valor CSLL"
            value={formatCurrency(dashboard?.valor_total_csll ?? 0)}
            helpText="CSLL retida (pode incluir PIS/COFINS retidos — NT-007)"
            icon={Receipt}
            iconColor="#319795"
            iconBg="#E6FFFA"
            isLoading={isFetchingDashboard}
          />
          <StatCard
            title="Valor INSS"
            value={formatCurrency(dashboard?.valor_total_inss ?? 0)}
            helpText="Total de INSS Retido"
            icon={Receipt}
            iconColor="#9F7AEA"
            iconBg="#FAF5FF"
            isLoading={isFetchingDashboard}
          />
        </SimpleGrid>
      </Box>

      {/* Listagem de Empresas */}
      <Flex justify="space-between" align="center" mb={4}>
        <HStack spacing={3}>
          <Text fontSize="lg" fontWeight="bold" color="gray.700">
            Empresas com NFS-e no Período
          </Text>
          <Text fontSize="sm" color="gray.500">
            ({filteredCompaniesList.length}{' '}
            {dashboardFilter !== 'all'
              ? `de ${dashboard?.empresas_detalhes?.length ?? 0}`
              : 'empresas'}
            )
          </Text>
        </HStack>
      </Flex>

      {/* Filtros */}
      <Box mb={4} p={4} bg="gray.50" borderRadius="lg">
        <FormProvider {...filtersFormMethods}>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'stretch', md: 'center' }}
            gap={4}
          >
            {/* Campos de Pesquisa */}
            <HStack spacing={2} flex={1} maxW={{ base: '100%', md: '500px' }}>
              <SearchModeBar
                modes={MODES_ID_NOME_CNPJ}
                mode={searchMode}
                onModeChange={m => setSearchMode(m as 'ID' | 'Nome' | 'CNPJ')}
                value={filtersFormMethods.watch('search') ?? ''}
                onValueChange={v => filtersFormMethods.setValue('search', v, { shouldDirty: true })}
              />
            </HStack>

            {/* Toggle Grid/List, Navegação de Mês e Botão Filtros */}
            <HStack spacing={3}>
              {/* Toggle Grid/List */}
              <HStack spacing={1}>
                <IconButton
                  aria-label="Visualização em grade"
                  icon={<Icon as={LayoutGrid} boxSize={5} />}
                  onClick={() => setViewMode('grid')}
                  variant={viewMode === 'grid' ? 'solid' : 'outline'}
                  colorScheme={viewMode === 'grid' ? 'blue' : 'gray'}
                  size="sm"
                />
                <IconButton
                  aria-label="Visualização em lista"
                  icon={<Icon as={List} boxSize={5} />}
                  onClick={() => setViewMode('list')}
                  variant={viewMode === 'list' ? 'solid' : 'outline'}
                  colorScheme={viewMode === 'list' ? 'blue' : 'gray'}
                  size="sm"
                />
              </HStack>
              <MonthNavigator
                currentDate={currentDate}
                onPrevMonth={handlePrevMonth}
                onNextMonth={handleNextMonth}
                onSelectMonth={handleSelectMonth}
              />
              <Button
                onClick={() => setIsFilterDrawerOpen(true)}
                leftIcon={<FunnelSimple size={16} />}
                variant="outline"
                colorScheme={hasActiveFilters ? 'blue' : 'gray'}
                height="38px"
              >
                Filtros{' '}
                {hasActiveFilters &&
                  `(${Array.from(filters.keys()).filter(key => key !== 'page' && key !== 'size' && key !== 'date_start' && key !== 'date_end' && key !== 'search' && filters.get(key)).length})`}
              </Button>
            </HStack>
          </Flex>

          {/* FilterDrawer para filtros avançados */}
          <FilterDrawer
            isOpen={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            title="Filtros Avançados"
            hasActiveFilters={hasActiveFilters}
            onClearFilters={handleClearFilters}
            onApplyFilters={handleApplyFilters}
            formMethods={filtersFormMethods}
            size="lg"
          >
            <div className="grid grid-cols-2 gap-4">
              <SelectInputForm<ISelect<string>>
                name="cidade"
                placeholder="Filtrar por Cidade"
                options={getFormattedLabelStringFromArray(NfseFilters?.cidade)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="uf"
                placeholder="Filtrar por Estado"
                options={getFormattedLabelStringFromArray(NfseFilters?.uf)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="grupo_de_empresas"
                placeholder="Filtrar por Grupo de Empresa"
                options={getFormattedLabelStringFromArray(NfseFilters?.grupo_de_empresas)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="tags"
                placeholder="Filtrar por Tags"
                options={getFormattedLabelStringFromArray(NfseFilters?.tags)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="comercial"
                placeholder="Filtrar por Comercial"
                options={getFormattedLabelStringFromArray(NfseFilters?.comercial)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="contabil"
                placeholder="Filtrar por Contábil"
                options={getFormattedLabelStringFromArray(NfseFilters?.contabil)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="financeiro"
                placeholder="Filtrar por Financeiro"
                options={getFormattedLabelStringFromArray(NfseFilters?.financeiro)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="contas_a_pagar"
                placeholder="Filtrar por Contas a Pagar"
                options={getFormattedLabelStringFromArray(NfseFilters?.contas_a_pagar)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="encerramento_anual"
                placeholder="Filtrar por Encerramento Anual"
                options={getFormattedLabelStringFromArray(NfseFilters?.encerramento_anual)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="fiscal"
                placeholder="Filtrar por Fiscal"
                options={getFormattedLabelStringFromArray(NfseFilters?.fiscal)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="imposto_de_renda"
                placeholder="Filtrar por Imposto de Renda"
                options={getFormattedLabelStringFromArray(NfseFilters?.imposto_de_renda)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="integracao_contabil"
                placeholder="Filtrar por Integração Contábil"
                options={getFormattedLabelStringFromArray(NfseFilters?.integracao_contabil)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="integracao_fiscal"
                placeholder="Filtrar por Integração Fiscal"
                options={getFormattedLabelStringFromArray(NfseFilters?.integracao_fiscal)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="pessoal"
                placeholder="Filtrar por Pessoal"
                options={getFormattedLabelStringFromArray(NfseFilters?.pessoal)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="relacionamento_com_cliente"
                placeholder="Filtrar por Relacionamento com Cliente"
                options={getFormattedLabelStringFromArray(NfseFilters?.relacionamento_com_cliente)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="societario"
                placeholder="Filtrar por Societário"
                options={getFormattedLabelStringFromArray(NfseFilters?.societario)}
                controlMb={0}
              />
            </div>
          </FilterDrawer>
        </FormProvider>
      </Box>

      {/* Renderização condicional: Grid ou List */}
      {viewMode === 'grid' ? (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={4}>
          {isFetchingDashboard &&
            Array.from({ length: 8 }).map((_, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <Skeleton key={`skeleton-${idx}`} height="250px" borderRadius="xl" />
            ))}
          {!isFetchingDashboard && filteredCompaniesList.length === 0 && (
            <Box gridColumn="1 / -1" textAlign="center" py={10}>
              <Text color="gray.500">
                {dashboardFilter !== 'all'
                  ? 'Nenhuma empresa encontrada com este filtro.'
                  : 'Nenhuma empresa com NFS-e encontrada no período selecionado.'}
              </Text>
            </Box>
          )}
          {!isFetchingDashboard &&
            filteredCompaniesList.length > 0 &&
            filteredCompaniesList.map(empresa => (
              <NfseCompanyCard
                key={empresa.empresa_id}
                empresa={empresa}
                onClick={handleCardClick}
              />
            ))}
        </SimpleGrid>
      ) : (
        <FormBox>
          <div className="p-4">
            <DataTable<INfseDashboardEmpresa>
              columns={columns}
              data={filteredCompaniesList}
              pagination={{
                total: filteredCompaniesList.length,
                page: 1,
                size: filteredCompaniesList.length || 50,
                pages: 1,
                next_page: null,
                previous_page: null,
              }}
              emptyMsg={
                dashboardFilter !== 'all'
                  ? 'Nenhuma empresa encontrada com este filtro.'
                  : 'Nenhuma empresa com NFS-e encontrada no período selecionado.'
              }
              loadingMsg="Carregando Empresas..."
              isFetching={isFetchingDashboard}
            />
          </div>
        </FormBox>
      )}
    </PageBox>
  );
}
